import React, { useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import { Box, Button, Tooltip } from "@material-ui/core";
import { useStyles } from "./styles";
import moment from "moment";
import DownloadIcon from "@mui/icons-material/Download";
import { mkConfig, generateCsv, download } from "export-to-csv";

interface Props {
  columns: any;
  rows?: any;
  height?: number;
  loader?: boolean;
  defaultPerPageRecord?: number;
  deletedRow?: boolean;
  hideFooter?: boolean;
  handlePrint?: any;
  isExport?: boolean;
  filename?: any;
  exportHandler?: any;
}

const CommonMaterialGridMultiSelect = ({
  columns,
  rows,
  height,
  loader = false,
  deletedRow = false,
  hideFooter = true,
  isExport = false,
  filename,
  exportHandler,
}: Props) => {
  const classes = useStyles();
  const emptyMessage = useMemo(() => {
    return <div className={classes.label}>No results found</div>;
  }, []);

  const csvConfig = mkConfig({
    fieldSeparator: ",",
    decimalSeparator: ".",
    useKeysAsHeaders: true,
    filename: filename || "default",
  });

  const handleExportData = async (data: any) => {
    const row = await data.map((row: any) => row.original);
    const records: any = await exportHandler(row);

    if (records) {
      const csv = generateCsv(csvConfig)(records);
      download(csvConfig)(csv);
    }
  };

  const muiTableBodyCellProps = (row: any) => {
    return {
      sx: deletedRow && row.row.original.isDeleted ? { color: "red" } : {},
    };
  };

  function truncateString(str: string | null | undefined, maxLength = 15) {
    if (str == null || typeof str !== "string") return "";
    if (str.length > maxLength) {
      return `${str.slice(0, maxLength)}...`;
    }
    return str;
  }
  const muiTableHeadCellProps = (row: any) => {
    const { containerWeightModel } = row.row.original;

    return (
      <table className={classes.tableNod}>
        <tr>
          <th className={classes.tableHeadNod}>Serial #</th>
          <th className={classes.tableHeadNod}>Container #</th>
          <th className={classes.tableHeadNod}>Cargo</th>
          {!row.row.original?.vehicleConsignmentType?.includes("Loose Cargo") && (
            <>
              <th className={classes.tableHeadNod}>Weight</th>
              <th className={classes.tableHeadNod}>Tare Weight</th>
            </>
          )}
          <th className={classes.tableHeadNod}>Cargo Weight</th>
          <th className={classes.tableHeadNod}>Date & Time</th>
          {/* <th className={classes.tableHeadNod}>Action</th> */}
        </tr>
        {containerWeightModel &&
          containerWeightModel?.map((item: any, index: number) => (
            <tr key={index}>
              <td className={classes.tableHeadNod}>{++index} </td>
              <td className={classes.tableHeadNod}>
                {item?.containerNumber ? item?.containerNumber : item?.containerConsignmentType}
              </td>
              <td className={classes.tableHeadNod}>
                <Tooltip title={item?.cargo} arrow>
                  <div>{truncateString(item?.cargo) || ""}</div>
                </Tooltip>
              </td>
              {!row.row.original?.vehicleConsignmentType?.includes("Loose Cargo") && (
                <>
                  <td className={classes.tableHeadNod}>
                    {`${item?.containerTotalWeight?.toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                    })} KG` || ""}
                  </td>
                  <td className={classes.tableHeadNod}>
                    {`${
                      item?.tareWeight &&
                      item?.tareWeight?.toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })
                    } KG` || ""}
                  </td>
                </>
              )}
              <td className={classes.tableHeadNod}>
                {`${
                  item?.cargoWeight &&
                  item?.cargoWeight?.toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                  })
                } KG` || ""}
              </td>
              <td className={classes.tableHeadNod}>
                {item.entryDate ? moment(item.entryDate).format("DD-MM-YYYY hh:mm:ss A") : ""}
              </td>

              {/* <td className={classes.tableHeadNod}>
                <NewCommonButton
                  onClick={() => handlePrint(item)}
                  icon={<PrintSharp />}
                  isGreen
                  disable
                >
                  Print
                </NewCommonButton>
              </td> */}
            </tr>
          ))}
      </table>
    );
  };

  return (
    <div className={classes.root}>
      <Box style={{ width: "100%", height: "100%" }}>
        <MaterialReactTable
          columns={columns}
          data={rows}
          enableExpanding
          enableColumnFilterModes
          enableColumnOrdering={true}
          enableGrouping
          enablePinning
          enableColumnResizing
          enableRowSelection={false}
          initialState={{ density: "compact" }}
          enableStickyHeader
          enableFullScreenToggle={false}
          enablePagination={hideFooter}
          // initialState={{ showColumnFilters: true }}
          state={{ isLoading: loader }}
          muiTableContainerProps={{
            sx: { height: height },
          }}
          renderEmptyRowsFallback={() => emptyMessage}
          muiTableBodyCellProps={muiTableBodyCellProps}
          muiTableHeadCellProps={{
            sx: () => ({
              background: "black",
              // borderRight: "white",
              color: "white",
            }),
          }}
          renderDetailPanel={(row: any) =>
            row?.row?.original?.vehicleConsignmentType &&
            row.row.original.vehicleConsignmentType.toLowerCase() === "export"
              ? null
              : muiTableHeadCellProps(row)
          }
          getRowCanExpand={({ original }: any) =>
            original?.vehicleConsignmentType &&
            original.vehicleConsignmentType.toLowerCase() !== "export"
          }
          renderTopToolbarCustomActions={({ table }) => (
            <div>
              {isExport ? (
                <Button
                  disabled={!rows?.length}
                  onClick={() => handleExportData(table.getPrePaginationRowModel().rows)}
                  startIcon={<DownloadIcon />}
                >
                  Export data to Excel
                </Button>
              ) : null}
            </div>
          )}
        />
      </Box>
    </div>
  );
};

export default CommonMaterialGridMultiSelect;
